/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import PortalIdParser from 'PortalIdParser';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { configureGetErrorTitle, configureGetErrorMessage } from 'error-messaging-js';
import { getContentTypeStringFromCategoryId, getContentAppPathFromCategoryId } from 'ContentEditorUI/utils/publishUtils';
import userInfo from 'hub-http/userInfo';
import { getLanguageNameFromKey } from 'ContentEditorUI/utils/multiLangUtils';
import { captureMessage } from 'ContentEditorUI/lib/exceptions';
import { EditorMetricsTracker } from 'ContentEditorUI/utils/metricsTracker';
const CLIENT_ERRORS = [412, 409, 404, 401];
const QUERY_TOO_LARGE_ERROR = 'QUERY_TOO_LARGE';
const OBJECT_ALREADY_EXISTS_ERROR = 'OBJECT_ALREADY_EXISTS';
const DUPLICATE_PATH_ERROR = 'TableRowValidationError.DUPLICATE_PATH';
const MODULE_NOT_FOUND_IN_RICH_TEXT_ERROR = 'MODULE_NOT_FOUND_IN_RICH_TEXT';
const ErrorAlertTypes = {
  SAVE: 'save'
};
const ErrorActionTypes = {
  SAVE: 'save',
  SAVE_TOO_LARGE: 'saveTooLarge',
  SAVE_DUPLICATE_PATH: 'saveDuplicatePath',
  SAVE_MODULE_NOT_FOUND: 'saveModuleNotFound'
};
export const getErrorTitle = configureGetErrorTitle({
  logSentry: true
});
export const getErrorMessage = configureGetErrorMessage({
  logSentry: true
});
const prefixKey = 'app.errors';
const reportedSaveErrors = {};
export const CONFLICT_ERRORS = 409;
export const showErrorWithHTML = data => {
  const {
    additionalTranslationOptions = {},
    errorAlertType
  } = data;
  FloatingAlertStore.addAlert({
    titleText: getErrorTitle(Object.assign({
      prefixKey
    }, data, additionalTranslationOptions)),
    message: getErrorMessage(Object.assign({
      prefixKey
    }, data, additionalTranslationOptions)),
    type: 'danger',
    errorAlertType
  });
};
export const isLocked = errorResponse => {
  return errorResponse && errorResponse.status === 412 && (!errorResponse.responseJSON || !errorResponse.responseJSON.retryable);
};
export const getAdditionalTranslationOptionsForSaveError = (responseJSON, categoryId, subcategory, contentDomain) => {
  if (!responseJSON) {
    return {};
  }
  const additionalTranslationOptions = {};
  const {
    errorTokens,
    context
  } = responseJSON;
  if (context) {
    additionalTranslationOptions.path = context.path;
  }
  additionalTranslationOptions.domain = contentDomain;
  const contentTypeToken = getContentTypeStringFromCategoryId(categoryId, subcategory);
  // default translated tokens to pass to message
  additionalTranslationOptions.portalId = PortalIdParser.get();
  additionalTranslationOptions.translatedContentType = contentTypeToken;
  if (errorTokens) {
    // specific tokens needing translations from API errorTokens
    const conflictCategoryId = errorTokens.CONFLICT_CATEGORY_ID;
    if (conflictCategoryId && conflictCategoryId.length) {
      additionalTranslationOptions.conflictCategory = getContentTypeStringFromCategoryId(conflictCategoryId[0]);
      additionalTranslationOptions.contentPath = getContentAppPathFromCategoryId(conflictCategoryId[0]);
      additionalTranslationOptions.contentType = contentTypeToken;
    }
    if (errorTokens.language && errorTokens.language.length > 0) {
      additionalTranslationOptions.language = errorTokens.language.map(getLanguageNameFromKey);
    }
    if (errorTokens.moduleId) {
      additionalTranslationOptions.moduleId = errorTokens.moduleId;
    }
    if (errorTokens.RESERVED_NAMESPACE) {
      additionalTranslationOptions.RESERVED_NAMESPACE = Array.isArray(errorTokens.RESERVED_NAMESPACE) ? errorTokens.RESERVED_NAMESPACE[0] : errorTokens.RESERVED_NAMESPACE;
    }
  }
  return additionalTranslationOptions;
};
export const clearSaveAlerts = () => {
  const alerts = FloatingAlertStore.getAlerts();
  Object.keys(alerts).forEach(key => {
    if (alerts[key].errorAlertType === ErrorAlertTypes.SAVE) {
      FloatingAlertStore.removeAlert(key);
    }
  });
};
const saveAction = errorResponse => {
  if ((errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.errorType) === QUERY_TOO_LARGE_ERROR) {
    return ErrorActionTypes.SAVE_TOO_LARGE;
  } else if ((errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.category) === OBJECT_ALREADY_EXISTS_ERROR && (errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.subCategory) === DUPLICATE_PATH_ERROR) {
    return ErrorActionTypes.SAVE_DUPLICATE_PATH;
  } else if ((errorResponse === null || errorResponse === void 0 ? void 0 : errorResponse.errorType) === MODULE_NOT_FOUND_IN_RICH_TEXT_ERROR) {
    return ErrorActionTypes.SAVE_MODULE_NOT_FOUND;
  }
  return ErrorActionTypes.SAVE;
};
const normalizeErrorTokens = responseJSON => {
  if (!(responseJSON !== null && responseJSON !== void 0 && responseJSON.errorTokens)) return responseJSON;
  return Object.assign({}, responseJSON, {
    errorTokens: Object.fromEntries(Object.entries(responseJSON.errorTokens).map(([k, v]) => [k, Array.isArray(v) && v.length ? v[0] : v]))
  });
};
const showSaveError = (response, category, subcategory) => {
  if (isLocked(response)) {
    return; // Handled by the LockedModal
  }
  const normalizedResponseJSON = normalizeErrorTokens(response.responseJSON);
  const normalizedResponse = normalizedResponseJSON !== response.responseJSON ? Object.assign({}, response, {
    responseJSON: normalizedResponseJSON
  }) : response;
  showErrorWithHTML({
    response: normalizedResponse,
    action: saveAction(normalizedResponseJSON),
    errorAlertType: ErrorAlertTypes.SAVE,
    additionalTranslationOptions: getAdditionalTranslationOptionsForSaveError(normalizedResponseJSON, category, subcategory)
  });
};
const logSaveError = errorResponse => {
  if (!errorResponse.responseJSON && errorResponse.status === undefined) {
    console.log(errorResponse);
    return;
  }
  if (errorResponse.status !== undefined && !reportedSaveErrors[errorResponse.status] && !CLIENT_ERRORS.includes(errorResponse.status)) {
    try {
      userInfo().then(({
        user
      }) => {
        const url = errorResponse.xhr && errorResponse.xhr.responseURL ? errorResponse.xhr.responseURL : '?';
        const extra = {
          url,
          responseText: errorResponse.responseText,
          errorMessage: errorResponse.errorMessage,
          user: user.email
        };
        EditorMetricsTracker.counter('content-save-errors').increment();
        captureMessage(`Content save error ${errorResponse.status}`, {
          extra
        });
        reportedSaveErrors[errorResponse.status] = true;
      });
    } catch (e) {
      return;
    }
  }
};
export const showAndLogSaveError = (response, category, subcategory) => {
  showSaveError(response, category, subcategory);
  logSaveError(response);
};